import React from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, LinearProgress
} from '@mui/material';

/**
 * Component to display user statistics dashboard
 * @param {Object} props
 * @param {Object} props.statistics - Statistics data object containing user metrics
 */
const UserStatistics = ({ statistics }) => {
  if (!statistics) return null;
  
  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Users
            </Typography>
            <Typography variant="h4">
              {statistics.totalUsers || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              New Users (30 Days)
            </Typography>
            <Typography variant="h4">
              {statistics.newUsersLast30Days || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Verified Users
            </Typography>
            <Typography variant="h4">
              {statistics.verifiedUsers || 0} 
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({statistics.verificationRate || 0}%)
              </Typography>
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Unverified Users
            </Typography>
            <Typography variant="h4">
              {statistics.unverifiedUsers || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={4}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Sign-up Methods
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">Google</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={statistics.totalUsers ? (statistics.googleUsers / statistics.totalUsers) * 100 : 0} 
                  sx={{ mt: 0.5, mb: 1.5, height: 8, borderRadius: 1 }}
                  color="primary"
                />
                
                <Typography variant="body2">Apple</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={statistics.totalUsers ? (statistics.appleUsers / statistics.totalUsers) * 100 : 0} 
                  sx={{ mt: 0.5, mb: 1.5, height: 8, borderRadius: 1 }}
                  color="secondary"
                />
                
                <Typography variant="body2">Email/Password</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={statistics.totalUsers ? (statistics.emailUsers / statistics.totalUsers) * 100 : 0} 
                  sx={{ mt: 0.5, mb: 0.5, height: 8, borderRadius: 1 }}
                  color="success"
                />
              </Box>
              <Box sx={{ ml: 2, minWidth: 60 }}>
                <Typography variant="body2" fontWeight="bold">{statistics.googleUsers || 0}</Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ my: 1.5 }}>{statistics.appleUsers || 0}</Typography>
                <Typography variant="body2" fontWeight="bold">{statistics.emailUsers || 0}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={8}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Email Verification Rate
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Overall Verification</Typography>
                <Typography variant="body2" fontWeight="bold">{statistics.verificationRate || 0}%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={statistics.verificationRate || 0} 
                sx={{ height: 10, borderRadius: 1, mb: 2 }}
                color="success"
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Chip 
                  label={`${statistics.verifiedUsers || 0} Verified`} 
                  color="success" 
                  size="small" 
                  variant="outlined" 
                />
                <Chip 
                  label={`${statistics.unverifiedUsers || 0} Unverified`} 
                  color="default" 
                  size="small" 
                  variant="outlined" 
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default UserStatistics;
